// src/connection/chatPhase.ts — what the AI panel's chat is doing, read from its events alone
// (agent-chat-speed Decision 5). Pure, so the composer hold (runInProgress), the typing receipt's
// label (AiPanelMessages) and the header's connection phase (AiPanelHeader) follow ONE rule.
// Unit-tested in chatPhase.test.ts.
import { AiEvent, AiEventType, BackgroundTask, ConnectionStatus } from "../types/ai-event";

export const STARTING_WORKSPACE_TEXT = "Starting workspace…";
export const THINKING_TEXT = "Thinking…";

export interface ChatPhase {
  // A Run — or the workspace start in front of it — is in progress: the composer is held.
  busy: boolean;
  // The workspace is starting: provisioning was signalled and neither status_running nor any of
  // Cas's output has arrived since.
  provisioning: boolean;
  // The typing receipt's text: the latest of a progress line (status_progress), "Starting
  // workspace…" (provisioning) or "Thinking…" (status_running); none once Cas has produced output
  // — the U.5 rule (AiPanelMessages, commit 55372be): progress text shows only until her first output.
  label?: string;
}

const IDLE: ChatPhase = { busy: false, provisioning: false };

// The Background Tasks running now: the newest session.background_tasks event holds the whole list, so an empty one clears it.
export function backgroundTasks(events: readonly AiEvent[]): BackgroundTask[] {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.type === AiEventType.BACKGROUND_TASKS) return e.tasks;
  }
  return [];
}

export function chatPhase(events: readonly AiEvent[]): ChatPhase {
  let labelSet = false;
  let label: string | undefined;
  let sawOutput = false;
  let sawError = false;
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    switch (e.type) {
      case AiEventType.STATUS_IDLE:
      case AiEventType.STATUS_TERMINATED:
        return IDLE;
      case AiEventType.SESSION_ERROR:
        sawError = true;
        break;
      case AiEventType.AGENT_MESSAGE:
      case AiEventType.TOOL_USE:
      case AiEventType.TOOL_RESULT:
        if (!labelSet) {
          labelSet = true;
          label = undefined;
        }
        sawOutput = true;
        break;
      case AiEventType.STATUS_PROGRESS:
        if (!labelSet) {
          labelSet = true;
          label = e.text;
        }
        break;
      case AiEventType.STATUS_RUNNING:
        return { busy: true, provisioning: false, label: labelSet ? label : THINKING_TEXT };
      case AiEventType.STATUS_PROVISIONING:
        // A session.error after the start began and before any output is the start failing
        // (ensureSession sends session.error and no status_idle — omega-ai-agent-service
        // src/server.ts session.create / user.message): the hold ends with it.
        if (sawError && !sawOutput) return IDLE;
        return { busy: true, provisioning: !sawOutput, label: labelSet ? label : STARTING_WORKSPACE_TEXT };
      default:
        break;
    }
  }
  return IDLE;
}

// The chat's one derived lifecycle: socket state, retry exhaustion, connection history, the
// loading marker and the event rule, in that precedence. The header, footer and composer all read
// this, so a stale event-only run can never look busy while the socket is down (spec Decision 2).
export type ChatLifecyclePhase =
  | "idle"
  | "connecting"
  | "resuming"
  | "loading-history"
  | "running"
  | "reconnecting"
  | "offline";

export interface ChatLifecycleInputs {
  status: ConnectionStatus;
  failed: boolean;
  hadConnected: boolean;
  loading: "resuming" | "loading-history" | null;
  events: readonly AiEvent[];
  activeSessionId: string | null;
}

export interface ChatLifecycle {
  phase: ChatLifecyclePhase;
  // The typing receipt is true only for a real Run on a connected socket.
  busy: boolean;
  // The composer is held for every phase except idle and running: a message typed while Cas works is queued and sent as the next turn.
  hold: boolean;
  // True while the thread is being assembled; the message list renders the skeleton.
  loading: boolean;
  caption: string;
}

export function lifecycleCaption(
  phase: ChatLifecyclePhase,
  activeSessionId: string | null
): string {
  switch (phase) {
    case "idle":
      return activeSessionId === null ? "Online" : "Connected";
    case "connecting":
      return "Connecting…";
    case "resuming":
      return "Resuming chat…";
    case "loading-history":
      return "Loading chat…";
    case "running":
      return "Connected";
    case "reconnecting":
      return "Reconnecting…";
    case "offline":
      return "Disconnected";
  }
}

export function chatLifecycle(input: ChatLifecycleInputs): ChatLifecycle {
  const chat = chatPhase(input.events);
  let phase: ChatLifecyclePhase;
  if (input.failed) {
    phase = "offline";
  } else if (
    input.status !== ConnectionStatus.CONNECTED &&
    (input.hadConnected || input.activeSessionId !== null || chat.busy)
  ) {
    phase = "reconnecting";
  } else if (input.status !== ConnectionStatus.CONNECTED) {
    phase = "connecting";
  } else if (input.loading === "resuming") {
    phase = "resuming";
  } else if (input.loading === "loading-history") {
    phase = "loading-history";
  } else if (chat.busy) {
    phase = "running";
  } else {
    phase = "idle";
  }
  return {
    phase,
    busy: phase === "running",
    hold: phase !== "idle" && phase !== "running",
    loading: phase === "resuming" || phase === "loading-history",
    caption: lifecycleCaption(phase, input.activeSessionId),
  };
}

// The header dot's phase. The socket state (ConnectionStatus, data-status) only says whether the
// WebSocket is open; this says whether Cas can answer now:
//   connecting   — socket not open, still retrying (Cas Loader)
//   failed       — retries exhausted (red, "Disconnected")
//   online       — socket open, no workspace ready (amber, "Online")
//   provisioning — the workspace is starting (amber, "Starting workspace…")
//   ready        — a Session is bound or a Run is live (green, "Connected")
export type ConnectionPhase = "connecting" | "failed" | "online" | "provisioning" | "ready";

export function connectionPhase(
  status: ConnectionStatus,
  failed: boolean,
  chat: ChatPhase,
  activeSessionId: string | null
): ConnectionPhase {
  if (status !== ConnectionStatus.CONNECTED) return failed ? "failed" : "connecting";
  if (chat.provisioning) return "provisioning";
  if (activeSessionId !== null || chat.busy) return "ready";
  return "online";
}
